import React, { useEffect, useRef, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { fmtDayName, fmtDayNum, fromMin, hospitalLabel, isWeekend, toMin, tint } from './scheduleUtils';

// Shared pieces for the hour-grid views: Timeline (a column per day) and Day
// (a column per doctor). Columns are identified by a `key` — a date or a uid.

export const HOUR_PX = 48;
const SNAP = 15;
const LAST_MIN = 24 * 60 - 1;
const snap = (m, step = SNAP) => Math.round(m / step) * step;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi));

// Default 06:00–20:00, stretched to fit anything earlier / later. hourPx lets a
// view shrink the hours to fit the screen (Day view); everything below reads it
// from the range.
export const hourRange = (timed, hourPx = HOUR_PX) => {
  let startHour = 6;
  let endHour = 20;
  timed.forEach((b) => {
    startHour = Math.min(startHour, Math.floor(toMin(b.bstart) / 60));
    endHour = Math.max(endHour, Math.ceil(toMin(b.bend) / 60));
  });
  endHour = Math.min(endHour, 24);
  return { startHour, endHour, hourPx, rangeStart: startHour * 60, rangeEnd: endHour * 60, height: (endHour - startHour) * hourPx };
};

export const gridBackgroundFor = (px) => ({
  backgroundImage: `repeating-linear-gradient(to bottom, #e2e8f0 0 1px, transparent 1px ${px}px),
    repeating-linear-gradient(to bottom, transparent 0 ${px / 2}px, #f1f5f9 ${px / 2}px ${px / 2 + 1}px, transparent ${px / 2 + 1}px ${px}px)`,
});
export const gridBackground = gridBackgroundFor(HOUR_PX);

export const timedTitle = (b, hospitalsById) => {
  const place = hospitalLabel(hospitalsById[b.bhid]);
  return b.btype === 'rooms' ? `Rooms${place ? ` · ${place}` : ''}` : place || 'Theatre list';
};

export const useNowMin = () => {
  const read = () => new Date().getHours() * 60 + new Date().getMinutes();
  const [now, setNow] = useState(read);
  useEffect(() => {
    const t = setInterval(() => setNow(read()), 60000);
    return () => clearInterval(t);
  }, []);
  return now;
};

// Clickable day heading — drills into the Day view.
export const DayHeader = ({ day, today, onOpen, className = '' }) => (
  <button
    type="button"
    onClick={() => onOpen(day)}
    title="Open this day"
    className={`px-2 py-2 text-center transition-colors hover:bg-teal-50 ${isWeekend(day) ? 'bg-slate-50' : 'bg-white'} ${className}`}
  >
    <div className={`text-[11px] font-semibold uppercase tracking-wide ${day === today ? 'text-brand-teal' : 'text-slate-400'}`}>{fmtDayName(day)}</div>
    <div className={`mx-auto mt-0.5 grid h-7 w-7 place-items-center rounded-full text-sm font-bold ${day === today ? 'bg-brand-gradient text-white' : 'text-slate-700'}`}>
      {fmtDayNum(day)}
    </div>
  </button>
);

export const HourGutter = ({ range }) => (
  <div className="relative" style={{ height: range.height }}>
    {Array.from({ length: range.endHour - range.startHour + 1 }, (_, i) => range.startHour + i).map((h) => (
      <div key={h} className="absolute right-2 text-[10px] font-medium text-slate-400" style={{ top: (h - range.startHour) * range.hourPx - 6 }}>
        {h === range.startHour ? '' : `${String(h).padStart(2, '0')}:00`}
      </div>
    ))}
  </div>
);

export const NowLine = ({ nowMin, range }) =>
  nowMin >= range.rangeStart && nowMin <= range.rangeEnd ? (
    <div className="pointer-events-none absolute left-0 right-0 z-10 h-0.5 bg-red-500" style={{ top: ((nowMin - range.rangeStart) / 60) * range.hourPx }}>
      <span className="absolute -left-1 -top-1 h-2.5 w-2.5 rounded-full bg-red-500" />
    </div>
  ) : null;

// Click, or drag down, an empty column to create. A plain click gives a
// two-hour slot starting on the half hour.
export const useCreateDrag = (range, onCreate) => {
  const [ghost, setGhost] = useState(null);
  const ref = useRef(null);

  const start = (e, key) => {
    if (e.button !== 0 || e.target.closest('[data-block]')) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const at = (clientY) => clamp(snap(range.rangeStart + ((clientY - rect.top) / range.hourPx) * 60), range.rangeStart, range.rangeEnd);
    const origin = at(e.clientY);
    ref.current = { key, a: origin, b: origin, moved: false };
    setGhost(ref.current);

    const move = (ev) => {
      const cur = at(ev.clientY);
      ref.current = { ...ref.current, b: cur, moved: ref.current.moved || cur !== origin };
      setGhost(ref.current);
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      const g = ref.current;
      ref.current = null;
      setGhost(null);
      let s = Math.min(g.a, g.b);
      let en = Math.max(g.a, g.b);
      if (!g.moved || en - s < 30) {
        s = Math.min(Math.floor(g.a / 30) * 30, 22 * 60);
        en = s + 120;
      }
      onCreate(g.key, fromMin(s), fromMin(Math.min(en, LAST_MIN)));
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  return { ghost, start };
};

export const Ghost = ({ ghost, range }) => {
  const s = Math.min(ghost.a, ghost.b);
  const en = Math.max(ghost.a, ghost.b, s + SNAP);
  return (
    <div
      className="pointer-events-none absolute left-1 right-1 z-20 rounded-md border-2 border-brand-teal bg-brand-teal/20 px-1.5 text-[11px] font-semibold text-brand-deep"
      style={{ top: ((s - range.rangeStart) / 60) * range.hourPx, height: ((en - s) / 60) * range.hourPx }}
    >
      {ghost.moved ? `${fromMin(s)}–${fromMin(en)}` : ''}
    </div>
  );
};

// Drag a timed session onto a column + time, keeping its length. Ctrl/Alt = copy.
export const useMoveDrag = (range, onDrop) => {
  const dragRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const duration = (b) => toMin(b.bend) - toMin(b.bstart);

  const startAt = (e, drag) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const raw = range.rangeStart + ((e.clientY - rect.top) / range.hourPx) * 60 - drag.offsetMin;
    return clamp(snap(raw), 0, LAST_MIN - duration(drag.block));
  };

  const blockProps = (b, editable) => ({
    draggable: editable,
    onDragStart: (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      dragRef.current = { block: b, offsetMin: ((e.clientY - rect.top) / range.hourPx) * 60 };
      e.dataTransfer.effectAllowed = 'copyMove';
      e.dataTransfer.setData('text/plain', String(b.bid));
    },
    onDragEnd: () => {
      dragRef.current = null;
      setPreview(null);
    },
  });

  const columnProps = (key, canDrop = () => true) => ({
    onDragOver: (e) => {
      const drag = dragRef.current;
      if (!drag) return;
      if (!canDrop(drag.block)) {
        setPreview(null);
        return;
      }
      e.preventDefault();
      e.dataTransfer.dropEffect = e.ctrlKey || e.altKey ? 'copy' : 'move';
      const start = startAt(e, drag);
      setPreview((p) => (p && p.key === key && p.start === start ? p : { key, start, block: drag.block }));
    },
    onDrop: (e) => {
      const drag = dragRef.current;
      if (!drag || !canDrop(drag.block)) return;
      e.preventDefault();
      const start = startAt(e, drag);
      dragRef.current = null;
      setPreview(null);
      onDrop(drag.block, key, fromMin(start), fromMin(start + duration(drag.block)), e.ctrlKey || e.altKey);
    },
  });

  return { preview, blockProps, columnProps, draggingBid: preview?.block.bid };
};

export const DropPreview = ({ preview, range, colour }) => {
  const dur = toMin(preview.block.bend) - toMin(preview.block.bstart);
  return (
    <div
      className="pointer-events-none absolute left-1 right-1 z-20 rounded-md border-2 border-dashed px-1.5 text-[11px] font-semibold text-slate-700"
      style={{ top: ((preview.start - range.rangeStart) / 60) * range.hourPx, height: (dur / 60) * range.hourPx, borderColor: colour, background: tint(colour, 0.12) }}
    >
      {fromMin(preview.start)}–{fromMin(preview.start + dur)}
    </div>
  );
};

export const TimedBlock = ({ block, col, cols, range, colour, label, title, detail, notes, conflict, dragging, onClick, dragProps }) => {
  const top = ((toMin(block.bstart) - range.rangeStart) / 60) * range.hourPx;
  const h = Math.max(((toMin(block.bend) - toMin(block.bstart)) / 60) * range.hourPx, 22);
  return (
    <div
      data-block
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter') onClick(); }}
      {...dragProps}
      title={[`${block.bstart}–${block.bend}`, title, detail, notes, conflict && `⚠ ${conflict}`].filter(Boolean).join('\n')}
      className={`absolute overflow-hidden rounded-md border-l-[3px] px-1.5 py-1 text-left shadow-sm transition-shadow hover:z-10 hover:shadow-md ${
        dragProps.draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'
      } ${conflict ? 'ring-1 ring-red-400' : ''} ${dragging ? 'opacity-40' : ''}`}
      style={{
        top: top + 1,
        height: h - 2,
        left: `calc(${(col / cols) * 100}% + 2px)`,
        width: `calc(${100 / cols}% - 4px)`,
        borderLeftColor: colour,
        background: `linear-gradient(${tint(colour, 0.16)}, ${tint(colour, 0.16)}), #fff`,
      }}
    >
      <div className="flex items-center gap-1 text-[10.5px] font-bold leading-4" style={{ color: colour }}>
        {label}
        <span className="truncate font-semibold text-slate-500">{block.bstart}–{block.bend}</span>
        {conflict && <ExclamationTriangleIcon className="ml-auto h-3.5 w-3.5 shrink-0 text-red-500" />}
      </div>
      <div className="truncate text-xs font-semibold leading-4 text-slate-800">{title}</div>
      {h > 60 && detail && <div className="truncate text-[10.5px] leading-4 text-slate-500">{detail}</div>}
      {h > 96 && notes && <div className="mt-0.5 line-clamp-2 text-[10.5px] leading-4 text-slate-400">{notes}</div>}
    </div>
  );
};
